<template>
  <q-form ref="formRef" greedy>
    <div class="text-h5 text-bold">ボタン</div>
    <div class="text-subtitle2">ボタンのスタイル・アクション設定</div>
    <div class="row q-mt-md">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>タイプ<span class="text-brand-red">*</span></div>
        <div class="text-caption">Type</div>
      </div>
      <q-select
        outlined
        dense
        emit-value
        map-options
        v-model="value.type"
        :options="typeList"
        class="col"
        disable
      >
        <template v-slot:selected-item="scope">
          <div class="column">
            <div>{{ scope.opt.label }}</div>
            <div class="text-caption">{{ scope.opt.value }}</div>
          </div>
        </template>
      </q-select>
    </div>
    <div class="row q-mt-sm" v-if="isAdmin">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>フレックス</div>
        <div class="text-caption">Flex</div>
      </div>
      <q-input
        outlined
        dense
        type="number"
        min="0"
        v-model.number="value.flex"
        placeholder="Num."
        class="col"
      />
    </div>
    <div class="row q-mt-sm" v-if="isAdmin">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>ポジション</div>
        <div class="text-caption">position</div>
      </div>
      <q-select
        outlined
        dense
        v-model="value.position"
        :options="['', 'relative', 'absolute']"
        class="col"
      />
    </div>
    <div class="row q-mt-sm" v-if="isAdmin">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>マージン</div>
        <div class="text-caption">margin</div>
      </div>
      <q-input
        outlined
        dense
        v-model="value.margin"
        :rules="[
          (val) => marginValidate(val) || 'invalid property',
        ]"
        placeholder="XXpx"
        class="col flex-input"
      >
        <template v-slot:append>
          <q-btn-dropdown
            no-caps
            unelevated
            color="brand-grey"
            label="px or keywords"
          >
            <q-list>
              <q-item
                v-for="(item, index) in marginList"
                clickable
                v-close-popup
                :key="index"
                @click="value.margin = item"
              >
                <q-item-section>
                  <q-item-label>{{ item }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-btn-dropdown>
        </template>
      </q-input>
    </div>
    <div class="row q-mt-sm" v-if="isAdmin">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>高さ</div>
        <div class="text-caption">height</div>
      </div>
      <q-select
        outlined
        dense
        v-model="value.height"
        :options="['', 'sm', 'md']"
        class="col"
      />
    </div>
    <div class="row q-mt-sm" v-if="isAdmin">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>スタイル</div>
        <div class="text-caption">style</div>
      </div>
      <q-select
        outlined
        dense
        v-model="value.style"
        :options="['', 'link', 'primary', 'secondary']"
        class="col"
      />
    </div>
    <div class="row q-mt-sm">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>カラー</div>
        <div class="text-caption">color</div>
      </div>
      <q-input
        outlined
        dense
        v-model="value.color"
        :rules="[
          (val) => colorValidate(val) || 'invalid property',
        ]"
        placeholder="#FFFFFF"
        class="col flex-input"
      >
        <template v-slot:append>
          <q-icon name="colorize" class="cursor-pointer">
            <q-popup-proxy cover transition-show="scale" transition-hide="scale">
              <q-color no-header no-footer v-model="value.color" />
            </q-popup-proxy>
          </q-icon>
        </template>
      </q-input>
    </div>
    <div class="row q-mt-sm" v-if="isAdmin">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>垂直位置</div>
        <div class="text-caption">gravity</div>
      </div>
      <q-select
        outlined
        dense
        v-model="value.gravity"
        :options="['', 'top', 'bottom', 'center']"
        class="col"
      />
    </div>
    <div class="row q-mt-sm" v-if="isAdmin">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>フォント拡縮</div>
        <div class="text-caption">scaling</div>
      </div>
      <q-select
        outlined
        dense
        emit-value
        map-options
        v-model="value.scaling"
        :options="trueFalseList"
        class="col"
      />
    </div>
    <div class="row q-mt-lg" v-if="isAdmin">
      <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
        <div>フォント調整</div>
        <div class="text-caption">adjustMode</div>
      </div>
      <q-select
        outlined
        dense
        v-model="value.adjustMode"
        :options="['', 'shrink-to-fit']"
        class="col"
        hint="`adjustMode` is not supported in simulator"
      />
    </div>
    <q-separator class="q-mt-lg q-mb-md" v-if="isAdmin" />
    <Offset v-model="value" v-if="isAdmin" />
    <q-separator class="q-my-md" />
    <Action v-model="value.action" :isAdmin="isAdmin" :isRequired="true" />
  </q-form>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import { maska } from 'maska'
import { Prop, Watch } from 'vue-property-decorator'
import { IFlexMessageButton, IFlexMessageURIAction } from '@/utils/types'
import Offset from '@/components/common/flex-message/forms/Offset.vue'
import Action from '@/components/common/flex-message/forms/Action.vue'

@Options({
  components: { Action, Offset },
  directives: { maska },
  emits: ['update:modelValue', 'update:validateValue'],
})
export default class Button extends Vue {
  @Prop()
  modelValue!: IFlexMessageButton

  @Prop()
  isAdmin!: boolean

  get value() {
    return this.modelValue
  }

  set value(value: IFlexMessageButton) {
    this.$emit('update:modelValue', value)
  }

  get typeList() {
    return [
      {
        label: 'ボタン',
        value: 'button',
      },
    ]
  }

  get marginList() {
    return ['none', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']
  }

  get trueFalseList() {
    return [
      {
        label: '',
        value: '',
      },
      {
        label: 'True',
        value: true,
      },
      {
        label: 'False',
        value: false,
      },
    ]
  }

  colorValidate(val) {
    if (!val) return true
    return /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(val)
  }

  marginValidate(val) {
    if (!val) return true

    const isPx = /^\d+(\.\d+)?px$/.test(val)
    const isKeyword = this.marginList.includes(val)
    return !(!isPx && !isKeyword)
  }

  @Watch('value', { deep: true })
  async validate() {
    await this.$nextTick(async () => {
      const isValid = await this.$refs.formRef.validate()
      if (isValid) {
        this.$emit('update:validateValue', true)
      } else {
        this.$emit('update:validateValue', false)
      }
      return isValid
    })
  }

  mounted() {
    if (this.value.action) {
      if (this.value.action.type === 'uri') {
        const uriAction: IFlexMessageURIAction = {
          type: 'uri',
          label: 'action',
          uri: 'http://linecorp.com/',
          altUri: {
            desktop: '',
          },
        }
        this.value.action = { ...uriAction, ...this.value.action }
      }
    }
    this.validate()
  }
}
</script>

<style scoped lang="scss">

</style>
